import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  ApiSavedView,
  createSavedViewApi,
  deleteSavedViewApi,
  listSavedViewsApi,
  updateSavedViewApi,
} from '@/lib/savedViewsApi';
import {
  CalendarConfig,
  ChartConfig,
  ColumnId,
  ConditionalFormatRule,
  DateFilter,
  DateFilterState,
  DisplayOptions,
  FilterState,
  FormatsState,
  RowSpacing,
  SortState,
  TotalsState,
  ViewMode,
} from './types';

export type GroupMode = 'detail' | 'summary';

export interface OperationsViewState {
  sort: SortState;
  filters: FilterState;
  dateFilters: DateFilterState;
  totals: TotalsState;
  formats: FormatsState;
  viewMode: ViewMode;
  display: DisplayOptions;
  /** Column ID to group rows by in table/cards views. Undefined = no grouping. */
  groupBy?: ColumnId;
  /**
   * 'detail' (default) shows group header + individual rows (collapsible).
   * 'summary' shows only group headers (with per-group totals if any are
   * enabled) — good for "totals by Customer" style reports.
   */
  groupMode?: GroupMode;
  calendarConfig?: CalendarConfig;
  chartConfig?: ChartConfig;
  /**
   * User-chosen column order. Columns listed here appear first in that order;
   * columns absent from the list fall back to their default registry order.
   */
  columnOrder?: ColumnId[];
  /** Per-column label overrides — when set, used in headers + the gear toggle. */
  columnLabels?: Record<ColumnId, string>;
  /** Per-column pixel widths (set by the resize handle). Unset = auto sizing. */
  columnWidths?: Record<ColumnId, number>;
}

export interface SavedView {
  id: string;
  name: string;
  state: OperationsViewState;
  ownerId: string;
  ownerEmail: string;
  isShared: boolean;
  isSystem: boolean;
  updatedAt?: string;
}

interface SerializedViewState {
  sort: SortState;
  filters: Record<string, string[]>;
  dateFilters?: Record<string, DateFilter>;
  totals: ColumnId[];
  formats: FormatsState;
  viewMode: ViewMode;
  display?: {
    rowSpacing: RowSpacing;
    zebra: boolean;
    hiddenColumns: ColumnId[];
  };
  groupBy?: ColumnId;
  groupMode?: GroupMode;
  calendarConfig?: CalendarConfig;
  chartConfig?: ChartConfig;
  columnOrder?: ColumnId[];
  columnLabels?: Record<ColumnId, string>;
  columnWidths?: Record<ColumnId, number>;
}

function serializeState(state: OperationsViewState): SerializedViewState {
  const filters: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(state.filters) as [ColumnId, Set<string>][]) {
    if (v && v.size > 0) filters[k] = Array.from(v);
  }
  const dateFilters: Record<string, DateFilter> = {};
  for (const [k, v] of Object.entries(state.dateFilters)) {
    if (v) dateFilters[k] = v;
  }
  return {
    sort: state.sort,
    filters,
    dateFilters,
    totals: Array.from(state.totals),
    formats: state.formats,
    viewMode: state.viewMode,
    display: {
      rowSpacing: state.display.rowSpacing,
      zebra: state.display.zebra,
      hiddenColumns: Array.from(state.display.hiddenColumns),
    },
    groupBy: state.groupBy,
    groupMode: state.groupMode,
    calendarConfig: state.calendarConfig,
    chartConfig: state.chartConfig,
    columnOrder: state.columnOrder,
    columnLabels: state.columnLabels,
    columnWidths: state.columnWidths,
  };
}

function deserializeState(raw: SerializedViewState): OperationsViewState {
  const filters: FilterState = {};
  for (const [k, arr] of Object.entries(raw.filters ?? {})) {
    filters[k as ColumnId] = new Set(arr);
  }
  const dateFilters: DateFilterState = {};
  for (const [k, v] of Object.entries(raw.dateFilters ?? {})) {
    if (v) dateFilters[k as ColumnId] = v;
  }
  // Migrate legacy single-rule formats to array, and the old `color` field
  // to per-channel bg/text/icon colors.
  const formats: FormatsState = {};
  for (const [k, v] of Object.entries(raw.formats ?? {}) as [string, unknown][]) {
    if (!v) continue;
    const arr = Array.isArray(v) ? v : [v];
    formats[k as ColumnId] = arr.map((r: any, idx: number) => {
      const out: any = { ...r };
      if (!out.id) out.id = `r_${k}_${idx}_${Math.random().toString(36).slice(2, 7)}`;
      if (out.color && !out.bgColor && !out.textColor) {
        out.bgColor = out.color;
        out.textColor = out.color;
        out.iconColor = out.color;
      }
      return out as ConditionalFormatRule;
    });
  }
  const rawSort = raw.sort as unknown;
  let sort: SortState = [];
  if (Array.isArray(rawSort)) sort = rawSort as SortState;
  else if (rawSort && typeof rawSort === 'object' && 'columnId' in (rawSort as any)) {
    sort = [rawSort as any];
  }
  return {
    sort,
    filters,
    dateFilters,
    totals: new Set(raw.totals ?? []),
    formats,
    viewMode: raw.viewMode ?? 'table',
    display: {
      rowSpacing: raw.display?.rowSpacing ?? 'tight',
      zebra: raw.display?.zebra ?? false,
      hiddenColumns: new Set(raw.display?.hiddenColumns ?? []),
    },
    groupBy: raw.groupBy,
    groupMode: raw.groupMode,
    calendarConfig: raw.calendarConfig,
    chartConfig: raw.chartConfig,
    columnOrder: raw.columnOrder,
    columnLabels: raw.columnLabels,
    columnWidths: raw.columnWidths,
  };
}

function fromApi(v: ApiSavedView): SavedView {
  return {
    id: v.id,
    name: v.name,
    state: deserializeState(v.state as SerializedViewState),
    ownerId: v.ownerId,
    ownerEmail: v.ownerEmail,
    isShared: v.isShared,
    isSystem: !!v.isSystem,
    updatedAt: v.updatedAt,
  };
}

export function useSavedViews(entityKey: string) {
  const { token, currentUser } = useAuth();
  const [views, setViews] = useState<SavedView[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await listSavedViewsApi(token, entityKey);
        if (!cancelled) setViews(data.map(fromApi));
      } catch (err) {
        console.error('Failed to load saved views', err);
        if (!cancelled) setViews([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entityKey, token]);

  const createView = useCallback(
    async (name: string, state: OperationsViewState, isShared: boolean): Promise<SavedView> => {
      const created = await createSavedViewApi(token, {
        entityKey,
        name,
        state: serializeState(state),
        isShared,
      });
      const view = fromApi(created);
      setViews((prev) => [...prev, view]);
      return view;
    },
    [token, entityKey]
  );

  const updateView = useCallback(
    async (id: string, patch: { state?: OperationsViewState; name?: string; isShared?: boolean }) => {
      const apiPatch: { name?: string; state?: unknown; isShared?: boolean } = {};
      if (patch.name !== undefined) apiPatch.name = patch.name;
      if (patch.state !== undefined) apiPatch.state = serializeState(patch.state);
      if (patch.isShared !== undefined) apiPatch.isShared = patch.isShared;
      const updated = await updateSavedViewApi(token, id, apiPatch);
      const view = fromApi(updated);
      setViews((prev) => prev.map((v) => (v.id === id ? view : v)));
      return view;
    },
    [token]
  );

  const deleteView = useCallback(
    async (id: string) => {
      await deleteSavedViewApi(token, id);
      setViews((prev) => prev.filter((v) => v.id !== id));
    },
    [token]
  );

  const currentUserId: string | undefined = (currentUser as any)?._id ?? (currentUser as any)?.id;

  return { views, loading, createView, updateView, deleteView, currentUserId };
}
